'use client'

import { memo, useCallback } from 'react'
import { Check } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { cn } from '@/lib/utils'
import { NinxlScrollReveal, NinxlStaggerReveal, NinxlStaggerItem } from './ninxl-scroll-reveal'
import { NinxlSectionBackdrop, NinxlSectionHeader } from './ninxl-graphics'
import { NINXL_PRICING_VERSION } from '../../../../../shared/ninxl-pricing-config'

type PricingTier = {
  id: 'free' | 'payg' | 'enterprise' | 'updates' | 'cloud'
  name: string
  price: string
  period?: string
  desc: string
  /** baby: Paid tiers show monthly cloud credits in the strip */
  credits?: string
  creditsLabel?: string
  creditsPct?: number
  cta: string
  details: string[]
  popular?: boolean
  badge?: string
  contactHref?: string
  comingSoon?: boolean
}

const PRICING_V2_TIERS: PricingTier[] = [
  {
    id: 'free',
    name: 'Free',
    price: '$0',
    desc: 'Full NINX desktop at the version you install.',
    cta: 'Download free',
    details: [
      'Core local tools, chat, and on-device voice',
      'On-device models and automations',
      'No paid app updates. Stay on your install build.',
    ],
  },
  {
    id: 'updates',
    name: 'Updates',
    price: '$2',
    period: '/mo',
    desc: 'Keep NINX current with every release.',
    popular: true,
    credits: '50 credits',
    creditsLabel: 'Monthly cloud credits',
    creditsPct: 25,
    cta: 'Coming soon',
    comingSoon: true,
    details: [
      'Everything in Free',
      'Automatic app updates',
      '50 monthly cloud credits',
    ],
  },
  {
    id: 'cloud',
    name: 'Cloud',
    price: '$8.99',
    period: '/mo',
    desc: 'Updates plus monthly cloud credit for hosted workloads.',
    badge: 'Cloud credit',
    credits: '200 credits',
    creditsLabel: 'Monthly cloud credits',
    creditsPct: 100,
    cta: 'Coming soon',
    comingSoon: true,
    details: [
      'Everything in Updates',
      '200 monthly cloud credits',
      'Use credits for cloud chat and cloud automations',
    ],
  },
]

const PRICING_V1_TIERS: PricingTier[] = [
  {
    id: 'free',
    name: 'Free',
    price: '$0',
    desc: 'Private, on-device NINX at the version you install.',
    cta: 'Download free',
    details: [
      'Core local tools, chat, and on-device voice',
      'On-device models and automations',
      'Keep using your installed build for free',
    ],
  },
  {
    id: 'payg',
    name: 'Pay as you go',
    price: 'At cost',
    period: 'provider rates',
    desc: 'Fund cloud usage only when you need it.',
    badge: 'No markup',
    popular: true,
    cta: 'Coming soon',
    comingSoon: true,
    details: [
      'No extra charge on provider fees',
      'Credits work across cloud chat and automations',
      'Any positive credit balance includes app updates',
    ],
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: 'Custom',
    desc: 'Deployment, security, and support tailored to your organization.',
    badge: 'Contact us',
    cta: 'Contact us',
    contactHref: 'mailto:enterprise@nin.in?subject=NINX%20Enterprise%20Pricing%20Inquiry',
    details: [
      'Custom deployment and integrations',
      'Team onboarding and priority support',
      'Security and procurement support',
    ],
  },
]

const ACTIVE_PRICING_TIERS =
  NINXL_PRICING_VERSION === 'v1' ? PRICING_V1_TIERS : PRICING_V2_TIERS

export const NinxlPricingSection = memo(function NinxlPricingSection({
  variant = 'default',
}: { variant?: 'default' | 'editorial' }) {
  const isTieredPricing = NINXL_PRICING_VERSION === 'v2'
  const goToPlansPage = useCallback(() => {
    window.location.href = '/ningenie/plans'
  }, [])

  if (variant === 'editorial') {
    return (
      <section id="pricing" className="nx-pricing" aria-labelledby="nx-pricing-title">
        <div className="nx-pricing-heading">
          <span className="nx-eyebrow">PRICING / YOUR CHOICE</span>
          <h2 id="nx-pricing-title">Start freely.<br /><em>Grow on your terms.</em></h2>
          <p>{isTieredPricing
            ? 'Use the desktop app free at your installed version. Choose updates and optional cloud credits when you need them.'
            : 'Work locally for free. Fund hosted models and automations when you need them, at the underlying provider rate.'}</p>
        </div>
        <NinxlStaggerReveal className="nx-pricing-grid" stagger={0.1}>
          {ACTIVE_PRICING_TIERS.map((tier) => (
            <NinxlStaggerItem key={tier.id} className="nx-plan-reveal">
            <article className={`nx-plan${tier.popular ? ' nx-plan-featured' : ''}`}>
              <div className="nx-plan-label"><h3>{tier.name}</h3>{tier.popular || tier.badge ? <span>{tier.badge ?? 'Recommended'}</span> : null}</div>
              <p className="nx-plan-description">{tier.desc}</p>
              <div className="nx-plan-price"><strong>{tier.price}</strong>{tier.period ? <span>{tier.period}</span> : null}</div>
              {tier.credits ? <p className="nx-plan-credits">{tier.credits} <span>included monthly</span></p> : null}
              <ul>{tier.details.map((detail) => <li key={detail}><Check size={16} aria-hidden="true" /><span>{detail}</span></li>)}</ul>
              {tier.comingSoon ? <button className="nx-plan-action" disabled><span>{tier.cta}</span></button> : tier.contactHref ? <a className="nx-plan-action" href={tier.contactHref}><span>{tier.cta}</span></a> : tier.id === 'free' ? <a className="nx-plan-action ninxl-button-shine" href="#download"><span>{tier.cta}</span></a> : <button className="nx-plan-action ninxl-button-shine" onClick={goToPlansPage}><span>{tier.cta}</span></button>}
            </article>
            </NinxlStaggerItem>
          ))}
        </NinxlStaggerReveal>
        <p className="nx-pricing-note">{isTieredPricing ? 'Free keeps your installed build. Paid plans apply across NINX, web, and mobile.' : 'Cloud usage draws from credits at the underlying provider rate. No subscription required.'}</p>
      </section>
    )
  }

  return (
    <section id="pricing" className="bg-white border-y border-slate-200 py-20 scroll-mt-20 relative overflow-x-hidden">
      <NinxlSectionBackdrop variant="pricing" />
      <div className="absolute inset-0 -z-10 bg-gradient-to-b from-[#21728d]/[0.03] via-transparent to-transparent pointer-events-none" />
      <div className="mx-auto max-w-6xl px-6 md:px-8">
        <NinxlScrollReveal className="mb-12">
          <NinxlSectionHeader
            label="Pricing"
            title={
              isTieredPricing
                ? 'Free to start. Pay for updates, or cloud when you need it.'
                : 'Free locally. Pay only when cloud works for you.'
            }
            description={
              isTieredPricing
                ? 'NINX desktop is free at your installed version. Paid tiers add automatic updates and optional cloud credits for hosted chat and automations.'
                : 'Top up when you need hosted models or automations. Provider fees pass through without markup, and a funded balance includes app updates.'
            }
          />
        </NinxlScrollReveal>

        <NinxlStaggerReveal
          className={cn(
            'grid items-stretch gap-5 pt-4',
            isTieredPricing ? 'md:grid-cols-3' : 'mx-auto max-w-6xl md:grid-cols-3',
          )}
          stagger={0.1}
        >
          {ACTIVE_PRICING_TIERS.map((tier) => (
            <NinxlStaggerItem key={tier.name}>
              <Card
                className={cn(
                  'ninxl-pricing-card relative flex h-full flex-col rounded-3xl transition-all duration-300',
                  tier.popular
                    ? 'border-[#21728d] bg-gradient-to-b from-[#21728d]/[0.04] to-white shadow-xl shadow-[#21728d]/10 ring-1 ring-[#21728d]/15 md:-translate-y-2'
                    : 'border-slate-200 bg-white hover:-translate-y-1 hover:shadow-xl',
                )}
              >
                {tier.popular ? (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-[#21728d] px-3 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white shadow-md">
                    {tier.badge ?? 'Most Popular'}
                  </div>
                ) : tier.badge ? (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full border border-[#21728d]/20 bg-white px-3 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#21728d] shadow-sm">
                    {tier.badge}
                  </div>
                ) : null}
                <CardHeader className="pb-2">
                  <div className="font-semibold text-xl tracking-tight">{tier.name}</div>
                  <div className="text-xs text-slate-500 mt-1">{tier.desc}</div>
                </CardHeader>
                <CardContent className="flex flex-1 flex-col pt-1">
                  <div className="mb-4">
                    <span className="text-5xl font-semibold tracking-tighter">{tier.price}</span>
                    {tier.period ? (
                      <span className="ml-1 text-sm text-slate-500">{tier.period}</span>
                    ) : null}
                  </div>

                  {tier.credits ? (
                    <div className="mb-6">
                      <div className="flex justify-between text-[10px] text-slate-500 mb-1.5">
                        <span>{tier.creditsLabel ?? 'Cloud credits'}</span>
                        <span className="font-semibold text-[#21728d]">{tier.credits}</span>
                      </div>
                      <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                        <div
                          className="ninxl-credits-bar h-full rounded-full bg-gradient-to-r from-[#21728d] to-[#3d9ab5]"
                          style={{ width: `${tier.creditsPct ?? 100}%` }}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="mb-6 h-[34px]" aria-hidden />
                  )}

                  <Button
                    asChild={Boolean(tier.contactHref) || tier.id === 'free'}
                    variant={tier.popular ? 'default' : 'outline'}
                    disabled={tier.comingSoon}
                    onClick={tier.contactHref || tier.comingSoon || tier.id === 'free' ? undefined : goToPlansPage}
                    className={cn(
                      'relative z-[1] w-full mb-6',
                      tier.popular
                        ? 'ninxl-button-shine bg-[#21728d] hover:bg-[#1a5f75] text-white'
                        : 'border-slate-200 bg-white hover:bg-slate-50 text-slate-900 hover:text-slate-900',
                    )}
                  >
                    {tier.contactHref ? <a href={tier.contactHref}>{tier.cta}</a> : tier.id === 'free' ? <a href="#download">{tier.cta}</a> : tier.cta}
                  </Button>
                  <ul className="mt-auto space-y-2.5 text-sm text-slate-600">
                    {tier.details.map((d) => (
                      <li key={d} className="flex items-start gap-2">
                        <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#21728d]" />
                        {d}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </NinxlStaggerItem>
          ))}
        </NinxlStaggerReveal>
        <p className="text-center text-xs text-slate-500 mt-8">
          {isTieredPricing
            ? 'Free tier keeps your installed build. Paid plans apply across NINX, web, and mobile.'
            : 'Cloud usage draws from credits at the underlying provider rate. No subscription required.'}
        </p>
      </div>
    </section>
  )
})
